import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Job } from '../../../models/job.model';
import { Item } from 'src/app/models/item.model';
import { Comment } from 'src/app/models/comment.model';
import { JobStatus } from '../../../models/job-status.model';
import { JobEta } from '../../../models/job-eta.model';
import { BriefComment } from 'src/app/models/brief-comment.model';
import { JobItem } from 'src/app/models/job-item.model';
import { MassCompletionOtherAssets } from 'src/app/models/mass-completion-other-assets.model';
import { ApiMethod } from 'src/app/decorators/api-method.decorator';
import { DocumentUpdate, JobDocument } from 'src/app/models/document.model';
import { AddJobDocument } from '../../ui/models/managed-documents.model';
import { AuthService } from '../../auth.service';


@Injectable({
	providedIn: 'root'
})
export class JobService {

	private baseJobUrl = `${environment.baseUrl}/job`;

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService) { }

	@ApiMethod()
	getJobDetails(token: string): Observable<Job> {
		return this.httpClient.get<Job>(`${this.baseJobUrl}/${token}`);
	}

	@ApiMethod()
	getJobChain(token: string): Observable<JobItem[]> {
		return this.httpClient.get<JobItem[]>(`${this.baseJobUrl}/${token}/jobs-chain`);
	}

	@ApiMethod()
	getJobCompletionResultOptions(token: string): Observable<Item<number, string>[]> {
		return this.httpClient.get<Item<number, string>[]>(`${this.baseJobUrl}/${token}/lookup/result-options`);
	}

	@ApiMethod()
	getJobNoteThread(token: string, specificJobId?: number): Observable<BriefComment[]> {
		return this.httpClient.get<BriefComment[]>(`${this.baseJobUrl}/${token}/notes${specificJobId ? `/${specificJobId}` : ''}`);
	}

	@ApiMethod()
	addJobComment(token: string, comment: Comment): Observable<void> {
		return this.httpClient.post<void>(`${this.baseJobUrl}/${token}/comments`, comment);
	}

	@ApiMethod()
	completeJob(token: string, payload: JobStatus): Observable<void> {
		return this.httpClient.put<void>(`${this.baseJobUrl}/${token}/complete`, payload);
	}

	@ApiMethod()
	changeJobEta(token: string, payload: JobEta): Observable<void> {
		return this.httpClient.put<void>(`${this.baseJobUrl}/${token}/change-eta`, payload);
	}

	@ApiMethod()
	getOtherAssetsForMassCompletion(token: string): Observable<MassCompletionOtherAssets> {
		return this.httpClient.get<MassCompletionOtherAssets>(`${this.baseJobUrl}/${token}/lookup/other-assets/${true}`);
	}

	@ApiMethod()
	getOtherAssets(token: string): Observable<MassCompletionOtherAssets> {
		return this.httpClient.get<MassCompletionOtherAssets>(`${this.baseJobUrl}/${token}/lookup/other-assets/${false}`);
	}

	@ApiMethod()
	canCompleteJob(token: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${this.baseJobUrl}/${token}/can-complete`);
	}

	@ApiMethod()
	getJobDocumentByJobDocumentId(token: string, jobDocumentId: number): Observable<JobDocument> {
		return this.httpClient.get<JobDocument>(`${this.baseJobUrl}/${token}/documents/jobDocument/${jobDocumentId}`);
	}

	@ApiMethod()
	getJobDocuments(token: string): Observable<JobDocument[]> {
		return this.httpClient.get<JobDocument[]>(`${this.baseJobUrl}/${token}/documents`);
	}

	@ApiMethod()
	updateManagedJobDocument(token: string, documentOid: string, documentId: number, jobDocumentId: number, documentUpdate: DocumentUpdate): Observable<void> {
		return this.httpClient.patch<void>(`${this.baseJobUrl}/${token}/managed-documents/${documentId}/${documentOid}/${jobDocumentId}`, documentUpdate, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	deleteManagedJobDocument(token: string, documentOid: string, documentId: number, jobDocumentId: number): Observable<void> {
		return this.httpClient.delete<void>(`${this.baseJobUrl}/${token}/managed-documents/${documentId}/${documentOid}/${jobDocumentId}`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	addJobDocument(token: string, jobDocument: AddJobDocument): Observable<void> {
		return this.httpClient.post<void>(`${this.baseJobUrl}/${token}/managed-documents/job-document`, jobDocument, { headers: this.authService.getAuthenticationHeader() });
	}
}
